import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, OnInit, TemplateRef, ViewChild, inject } from '@angular/core';
import {
  Alert,
  AlertService,
  AlertType,
  ModalComponent,
  ModalResult,
  PaginatorFetcher,
  PaginatorFilters,
  TableComponent,
  TableConfig
} from '@michelin/theme';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslationService } from '../../../../shared/translations/services/translation.service';
import { Store } from '@ngxs/store';
import { ActivatedRoute, Router } from '@angular/router';
import { FeatureflagsService } from '../../../../shared/services/featureflags.service';
import { ROLES } from '../../../authentication/authRoles.constants';
import { hasRolePermission } from '../../../user-management/utils/UserSetupUtils';
import {
  PaginatedPreferredTreadResponse,
  Pagination,
  PreferredTread,
  PreferredTreadRow,
  PreferredTreadWithSpecRequest,
  PreferredTreadWithSpecResponse
} from '../../models/response.model';
import { FEATURE_FLAGS } from '../../../../shared/constants/constants';
import { TranslationState } from '../../../../shared/state/TranslationState';
import { DealerState } from '../../../../shared/state/DealerState';
import { map, of } from 'rxjs';
import { ProductsState } from '../../../../shared/state/ProductsState';
import { ProductDataService } from '../../services/product-data.service';

@Component({
  selector: 'app-preferred-treads-setup',
  templateUrl: './preferred-treads-setup.component.html',
  styleUrl: './preferred-treads-setup.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PreferredTreadsSetupComponent implements OnInit {
  @ViewChild(TableComponent, { static: true }) tableRef: TableComponent<Element>;
  @ViewChild('deleteConfirmation', { static: true }) deleteModal: ModalComponent;
  @ViewChild('deleteContent') deleteContent: TemplateRef<any>;
  @ViewChild('discardConfirmation', { static: true }) discardModal: ModalComponent;
  @ViewChild('discardContent') discardContent: TemplateRef<any>;
  public fb = inject(FormBuilder);
  public translationService = inject(TranslationService);
  public productDataService = inject(ProductDataService);
  public cdr = inject(ChangeDetectorRef);
  public store = inject(Store);
  public route = inject(ActivatedRoute);
  public router = inject(Router);
  public featureFlagsService = inject(FeatureflagsService);
  public alertService = inject(AlertService);

  pagePreferredTreadSpecificationPk: string;
  pagePreferredTreadCode: string;
  pagePreferredTreadDescription: string;
  initialFilterValues: any; // Store initial filter form values
  filterForm: FormGroup;
  filterFormBackend: FormGroup;
  saveEnabled = false;
  isModified = false;
  productSaveDisabled = true;
  pagination: Pagination;
  currentPage = 1;
  pageSize = 10;
  pageSizeOptions = [5, 10, 15, 25, 50];
  preferredTreadList: PreferredTreadRow[] = [];
  locallyAddedRowsList: PreferredTreadRow[] = [];
  displayedRowsForm: FormGroup = this.fb.group({ rows: this.fb.array([]) });
  makeList: string[] = [];
  axleList: string[] = [];
  applicationList: string[] = [];
  casingSizeList: string[] = [];
  preferredTreadSpecificationList: string[] = [];
  rankList: string[] = [];
  itemToDelete: PreferredTreadRow | null = null;
  private localIdCounter = 0;
  private starterRowSeeded = false;

  tableConfig: TableConfig<PreferredTread> = {
    paginator: {
      pageSize: this.pageSize,
      pageSizeOptions: this.pageSizeOptions,
      forceBottomAction: true,
      position: 'bottom',
      pageLabelOptions: {
        firstPageLabel: '',
        lastPageLabel: '',
        nextPageLabel: '',
        previousPageLabel: '',
        itemsPerPageLabel: this.translationService.translate('pageLabelOptionText')
      }
    },
    columns: {}
  };
  state = TableConfig.getState(this.tableConfig, this.route);

  constructor() {
    this.filterForm = this.fb.group({
      preferredTreadSpecificationPk: [''],
      code: [''],
      description: ['']
    });
    this.filterFormBackend = this.fb.group({
      preferredTreadSpecificationPk: [''],
      code: [''],
      description: ['']
    });
  }

  ngOnInit(): void {
    this.pagePreferredTreadSpecificationPk = this.route.snapshot.paramMap.get('preferredTreadPk') || '';
    const navigationState = this.router.getCurrentNavigation()?.extras?.state || history.state;
    if (navigationState) {
      this.pagePreferredTreadCode = navigationState.code;
      this.pagePreferredTreadDescription = navigationState.description;
    }
    this.filterForm.patchValue({
      preferredTreadPk: this.pagePreferredTreadSpecificationPk || '',
      code: this.pagePreferredTreadCode || '',
      description: this.pagePreferredTreadDescription || ''
    });

    // Store initial filter values for comparison
    this.initialFilterValues = {
      code: this.pagePreferredTreadCode || '',
      description: this.pagePreferredTreadDescription || ''
    };

    this.updateDropdownLists();
    this.productSaveDisabled = this.featureFlagsService.getFeatureFlagValue(FEATURE_FLAGS.IS_DUAL_WRITE_ENABLED);
    if (this.productSaveDisabled) {
      this.alertService.showAlert({
        type: AlertType.classical,
        title: this.translationService.translate('readOnlyProductDataHeader'),
        content: this.translationService.translate('readOnlyProductDataText'),
        timestamp: 60000
      } as Alert);
    }
    this.saveEnabled = !this.productSaveDisabled && hasRolePermission(ROLES.edit_product);
    this.store.select(TranslationState.currentLanguage).subscribe(() => {
      this.tableConfig.columns = {
        make: {
          cell: 'make',
          header: this.translationService.translate('makeText')
        },
        size: {
          cell: 'size',
          header: this.translationService.translate('sizeText') + '*'
        },
        application: {
          cell: 'application',
          header: this.translationService.translate('applicationText')
        },
        axle: {
          cell: 'axle',
          header: this.translationService.translate('axleText')
        },
        treadDesc: {
          cell: 'treadDesc',
          header: this.translationService.translate('preferredTreadText') + '*'
        },
        rank: {
          cell: 'preferredTreadRank',
          header: this.translationService.translate('rankColText') + '*'
        },
        action: {
          cell: 'action',
          header: this.translationService.translate('actionText')
        }
      };
      this.cdr.markForCheck();
    });
    this.rankList = Array.from({ length: 9 }, (_, i) => (i + 1).toString());
    this.displayedRowsForm.valueChanges.subscribe(() => {
      this.isModified = this.checkIfModified();
    });
    this.filterForm.valueChanges.subscribe(() => {
      this.isModified = this.checkIfModified();
    });
  }

  updateDropdownLists(): void {
    this.makeList.push(...this.store.selectSnapshot(ProductsState.products).makes);
    this.axleList.push(...this.store.selectSnapshot(ProductsState.products).axles);
    this.applicationList.push(...this.store.selectSnapshot(ProductsState.products).applications);
    this.casingSizeList.push(...this.store.selectSnapshot(ProductsState.products).sizes);
    this.preferredTreadSpecificationList.push(...this.store.selectSnapshot(ProductsState.products).treads);
  }

  fetcher: PaginatorFetcher<PreferredTread> = (filters: PaginatorFilters) => {
    this.currentPage = filters.page;
    this.pageSize = filters.limit;
    this.generateFilterForm();
    if (this.pagePreferredTreadSpecificationPk === '') {
      // Seeded once only; re-seeding here would resurrect the starter row the user just deleted
      if (!this.starterRowSeeded && this.locallyAddedRowsList.length === 0 && this.preferredTreadList.length === 0) {
        this.starterRowSeeded = true;
        const currentDealer = this.store.selectSnapshot(DealerState.currentDealer);
        this.preferredTreadList = [
          {
            _localId: this.nextLocalId(),
            preferredTreadPk: null,
            casingMake: null,
            casingSize: null,
            application: null,
            axle: null,
            preferredTreadSpecificationPk: null,
            preferredTreadRank: null,
            treadDesc: null,
            dealerFk: currentDealer?.id || ''
          }
        ];
      }

      const items = [...this.locallyAddedRowsList, ...this.preferredTreadList];
      this.updateDisplayedRowsForm(items);

      return of({ items, count: items.length });
    } else {
      return this.productDataService
        .getPreferredTreadSpecifications(
          {
            ...this.filterFormBackend.value,
            filters
          },
          filters.page,
          filters.limit,
          this.pagePreferredTreadSpecificationPk
        )
        .pipe(
          map((response: PaginatedPreferredTreadResponse) => {
            this.preferredTreadList = response.data.preferredTreads.map((item) => ({
              _localId: this.nextLocalId(),
              preferredTreadPk: item.preferredTreadPk ?? null,
              casingMake: item.casingMake ?? null,
              casingSize: item.casingSize ?? null,
              application: item.application ?? null,
              axle: item.axle ?? null,
              preferredTreadSpecificationPk: item.preferredTreadSpecificationPk ?? null,
              preferredTreadRank: item.preferredTreadRank ?? null,
              treadDesc: item.treadDesc ?? null,
              dealerFk: item.dealerFk ?? null
            }));
            this.pagination = response.data.pagination;

            const count = [...this.locallyAddedRowsList, ...this.preferredTreadList].length;
            this.updateDisplayedRowsForm([...this.locallyAddedRowsList, ...this.preferredTreadList]);
            return {
              items: [...this.locallyAddedRowsList, ...this.preferredTreadList],
              count
            };
          })
        );
    }
  };

  private nextLocalId(): string {
    return `row-${++this.localIdCounter}`;
  }

  updateDisplayedRowsForm(items: PreferredTreadRow[]): void {
    const rowForms = items.map((row) => this.createRowForm(row));
    const formArray = this.fb.array(rowForms);
    this.displayedRowsForm.setControl('rows', formArray);
  }

  createRowForm(row: PreferredTread): FormGroup {
    return this.fb.group({
      application: [row.application],
      axle: [row.axle],
      casingMake: [row.casingMake],
      casingSize: [row.casingSize],
      dealerFk: [row.dealerFk],
      preferredTreadPk: [row.preferredTreadPk],
      preferredTreadRank: [row.preferredTreadRank, [Validators.required]],
      preferredTreadSpecificationPk: [row.preferredTreadSpecificationPk],
      treadDesc: [row.treadDesc]
    });
  }

  generateFilterForm(): void {
    this.filterFormBackend.patchValue({
      preferredTreadSpecificationPk: this.filterForm.get('preferredTreadSpecificationPk')?.value || '',
      code: this.filterForm.get('code')?.value || '',
      description: this.filterForm.get('description')?.value || ''
    });
  }

  getControl(item: PreferredTreadRow, controlName: keyof PreferredTread): FormControl {
    const combinedList = [...this.locallyAddedRowsList, ...this.preferredTreadList];
    // Match on the UI-only row id: new rows all share a null preferredTreadPk and would otherwise collapse onto one form group
    const index = combinedList.findIndex((row) => row._localId === item._localId);

    if (index === -1) {
      return new FormControl('');
    }

    const rowsFormArray = this.displayedRowsForm.get('rows') as FormArray;

    // Check if FormArray exists and has the required index
    if (!rowsFormArray || index >= rowsFormArray.length) {
      return new FormControl('');
    }

    const control = rowsFormArray.at(index)?.get(controlName);
    return control ? (control as FormControl) : new FormControl('');
  }

  save() {
    if (!this.saveEnabled) {
      return;
    }

    if (!this.checkIfModified()) {
      this.alertService.showAlert({
        type: AlertType.strong,
        content: this.translationService.translate('noModifiedText')
      } as Alert);
      return;
    }

    const modifiedRows: PreferredTread[] = [];
    const formArray = this.displayedRowsForm.get('rows') as FormArray;
    const allFormValues = formArray.value.map((val: any, idx: number) => ({ ...val, index: idx }));

    for (let i = 0; i < formArray.length; i++) {
      const current = formArray.at(i).value;

      if (this.isBlankRow(current)) {
        continue;
      }

      if (!current.casingSize || !current.preferredTreadRank || !current.treadDesc) {
        this.alertService.showAlert({
          type: AlertType.critical,
          content:
            this.translationService.translate('rankColText') +
            ', ' +
            this.translationService.translate('sizeText') +
            ', ' +
            this.translationService.translate('preferredTreadText') +
            ' ' +
            this.translationService.translate('requiredText')
        } as Alert);
        return;
      }

      const isValidRank = this.validateRank(current, i, allFormValues);

      if (!isValidRank.isValid) {
        this.alertService.showAlert({
          type: AlertType.critical,
          content: isValidRank.errorMessage!
        } as Alert);
        return;
      }
    }

    // Check for duplicate combinations of make, size, application, axle, and tread
    const duplicateValidation = this.validateUniqueRowCombinations(allFormValues);
    if (!duplicateValidation) {
      this.alertService.showAlert({
        type: AlertType.critical,
        content: this.translationService.translate('duplicatePreferredTreadText')
      } as Alert);
      return;
    }

    const currentDealer = this.store.selectSnapshot(DealerState.currentDealer);
    const dealerId = currentDealer?.id || '';

    const totalRows = [...this.locallyAddedRowsList, ...this.preferredTreadList];
    for (let i = 0; i < totalRows.length; i++) {
      const original = totalRows[i];
      const current = formArray.at(i).value;

      if (this.isBlankRow(current)) {
        continue;
      }

      const isModified = Object.keys(current).some((key) => {
        const normalizedCurrent = this.normalizeValue(current[key]);
        const normalizedOriginal = this.normalizeValue(original[key]);
        return normalizedCurrent !== normalizedOriginal;
      });
      // Never-persisted rows are always upserts; diffing them against a synced baseline would drop them
      if (!original.preferredTreadPk || isModified) {
        modifiedRows.push({ ...original, ...current });
      }
    }

    const payload: PreferredTreadWithSpecRequest = {
      specification: {
        preferredTreadSpecificationPk: this.pagePreferredTreadSpecificationPk || null,
        dealerFk: dealerId,
        preferredTreadCode: this.filterForm.get('code')?.value || null,
        preferredTreadDescription: this.filterForm.get('description')?.value || null
      },
      // Only new and modified rows; the endpoint is upsert-only, omission never deletes
      preferredTreads: modifiedRows.map((row) => ({
        preferredTreadPk: row.preferredTreadPk ?? null,
        dealerFk: dealerId,
        casingMake: row.casingMake ?? null,
        casingSize: row.casingSize ?? null,
        application: row.application ?? null,
        axle: row.axle ?? null,
        preferredTreadSpecificationPk: this.pagePreferredTreadSpecificationPk || null,
        preferredTreadRank: row.preferredTreadRank != null ? String(row.preferredTreadRank) : null,
        treadDesc: row.treadDesc ?? null
      }))
    };

    this.productDataService.updatePreferredTreadWithSpec(payload).subscribe({
      next: (response: PreferredTreadWithSpecResponse) => {
        const specificationPk = response?.data?.specification?.preferredTreadSpecificationPk;
        if (specificationPk) {
          this.pagePreferredTreadSpecificationPk = specificationPk;
        }

        // Re-fetch from the server rather than merging locally; the spec PK is now known
        this.locallyAddedRowsList = [];
        this.tableRef.resetFilters();
        this.tableRef.patchExtraFilters(this.filterFormBackend.value, true);

        // Update initial filter values to reflect the saved state
        this.initialFilterValues = {
          code: this.filterForm.get('code')?.value || '',
          description: this.filterForm.get('description')?.value || ''
        };

        this.isModified = false;
        this.cdr.markForCheck();
        this.alertService.showAlert({
          type: AlertType.success,
          content: this.translationService.translate('camSaveSuccessText')
        } as Alert);
      },
      error: (error) => {
        if (error.status === 409) {
          this.alertService.showAlert({
            type: AlertType.critical,
            content: this.translationService.translate('codeText') + ' ' + this.translationService.translate('alreadyExistsText')
          } as Alert);
        } else {
          console.error('Error executing requests:', error);
          this.alertService.showAlert({
            type: AlertType.critical,
            content: this.translationService.translate('criticalErrorAlertText')
          } as Alert);
        }
      }
    });
  }

  private validateRank(current: any, index: number, allCurrentValues: any[]): { isValid: boolean; errorMessage?: string } {
    const rank = Number(current.preferredTreadRank);
    if (!current.preferredTreadRank) {
      return {
        isValid: false,
        errorMessage: this.translationService.translate('plainRankText') + this.translationService.translate('requiredText')
      };
    }
    if (!Number.isInteger(rank) || rank < 1 || rank > 9) {
      return { isValid: false, errorMessage: this.translationService.translate('rankInvalidText') };
    }
    if (allCurrentValues.some((item, idx) => idx !== index && item.preferredTreadRank == rank)) {
      return { isValid: false, errorMessage: this.translationService.translate('duplicateRankText') };
    }
    return { isValid: true };
  }

  private validateUniqueRowCombinations(allFormValues: any[]): boolean {
    const combinations = new Set<string>();

    for (let i = 0; i < allFormValues.length; i++) {
      const current = allFormValues[i];

      // Skip rows that don't have all required data
      if (!current.casingSize || !current.preferredTreadRank || !current.treadDesc) {
        continue;
      }

      // Create a unique combination key based on make, size, application, axle, and tread
      const combinationKey = `${current.casingMake || ''}-${current.casingSize || ''}-${current.application || ''}-${current.axle || ''}-${
        current.treadDesc || ''
      }`;

      if (combinations.has(combinationKey)) {
        return false;
      }

      combinations.add(combinationKey);
    }

    return true;
  }

  addTread(): void {
    this.syncFormIntoRows();
    const currentDealer = this.store.selectSnapshot(DealerState.currentDealer);

    // PK is minted by the backend on save
    this.locallyAddedRowsList.push({
      _localId: this.nextLocalId(),
      preferredTreadPk: null,
      dealerFk: currentDealer?.id || '',
      casingMake: null,
      casingSize: null,
      application: null,
      axle: null,
      preferredTreadSpecificationPk: this.pagePreferredTreadSpecificationPk || null,
      preferredTreadRank: null,
      treadDesc: null
    });
    this.tableRef.resetFilters();
    this.tableRef.patchExtraFilters(this.filterFormBackend.value, true);
    this.cdr.markForCheck();
  }

  private normalizeValue(value: any): any {
    return value === undefined || value === '' ? null : value;
  }

  // An untouched row is not a validation failure — it is simply not part of the save
  private isBlankRow(row: any): boolean {
    return !(
      row.preferredTreadPk ||
      row.casingMake ||
      row.casingSize ||
      row.application ||
      row.axle ||
      row.preferredTreadRank ||
      row.treadDesc
    );
  }

  // The fetcher rebuilds the form array from the raw row objects, so pending edits must be written back first
  private syncFormIntoRows(): void {
    const formArray = this.displayedRowsForm.get('rows') as FormArray;
    if (!formArray) {
      return;
    }

    const combinedItems = [...this.locallyAddedRowsList, ...this.preferredTreadList];
    for (let i = 0; i < formArray.length && i < combinedItems.length; i++) {
      const current = formArray.at(i).value;
      const row = combinedItems[i];
      Object.keys(current).forEach((key) => {
        row[key] = this.normalizeValue(current[key]);
      });
    }
  }

  checkIfModified(): boolean {
    const formArray = this.displayedRowsForm.get('rows') as FormArray;
    const combinedItems = [...this.locallyAddedRowsList, ...this.preferredTreadList];
    let modified = false;

    for (let i = 0; i < formArray.length; i++) {
      const original = combinedItems[i];
      const current = formArray.at(i).value;
      const hasData = !!(
        current.preferredTreadPk ||
        current.casingMake ||
        current.casingSize ||
        current.application ||
        current.axle ||
        current.preferredTreadRank ||
        current.treadDesc
      );

      if (
        hasData &&
        (!original.preferredTreadPk ||
          Object.keys(current).some((key) => {
            const normalizedCurrent = this.normalizeValue(current[key]);
            const normalizedOriginal = this.normalizeValue(original[key]);
            return normalizedCurrent !== normalizedOriginal;
          }))
      ) {
        modified = true;
        break;
      }
    }

    // Check if filter form is modified (code and description)
    if (!modified && this.initialFilterValues) {
      const currentCode = this.filterForm.get('code')?.value || '';
      const currentDescription = this.filterForm.get('description')?.value || '';
      const initialCode = this.initialFilterValues.code || '';
      const initialDescription = this.initialFilterValues.description || '';

      modified = currentCode !== initialCode || currentDescription !== initialDescription;
    }
    return modified || this.locallyAddedRowsList.length > 0;
  }

  showDeleteModal(item: PreferredTreadRow): void {
    this.deleteModal.open({
      id: 'deleteConfirmation',
      title: this.translationService.translate('warningText'),
      width: 550,
      content: this.deleteContent,
      confirm: this.translationService.translate('yesText'),
      cancel: this.translationService.translate('noText')
    });
    this.itemToDelete = item;
  }

  onDeleteModalClosed(result: ModalResult): void {
    if (result) {
      if (result.id === 'deleteConfirmation' && result.result) {
        this.deleteRow(this.itemToDelete);
      } else {
        this.itemToDelete = null;
      }
    }
  }

  deleteRow(item: PreferredTreadRow): void {
    // Never persisted (locally added or the new-spec starter row): drop it client-side, no HTTP
    if (!item.preferredTreadPk) {
      this.syncFormIntoRows();
      const localIndex = this.locallyAddedRowsList.findIndex((row) => row._localId === item._localId);
      if (localIndex !== -1) {
        this.locallyAddedRowsList.splice(localIndex, 1);
      } else {
        const listIndex = this.preferredTreadList.findIndex((row) => row._localId === item._localId);
        if (listIndex !== -1) {
          this.preferredTreadList.splice(listIndex, 1);
        }
      }
      this.tableRef.resetFilters();
      this.tableRef.patchExtraFilters(this.filterFormBackend.value, true);
      this.cdr.markForCheck();
      return;
    }

    this.syncFormIntoRows();
    this.productDataService.deletePreferredTread(item.preferredTreadPk).subscribe({
      next: () => {
        this.tableRef.resetFilters();
        this.tableRef.patchExtraFilters(this.filterFormBackend.value, true);
      }
    });
  }

  showDiscardModal(): void {
    this.discardModal.open({
      id: 'discardConfirmation',
      title: this.translationService.translate('discardChangesText'),
      width: 550,
      content: this.discardContent,
      confirm: this.translationService.translate('okText'),
      cancel: this.translationService.translate('cancelText')
    });
  }

  onDiscardModalClosed(result: ModalResult): void {
    if (result) {
      if (result.id === 'discardConfirmation' && result.result) {
        this.locallyAddedRowsList = [];
        this.updateDisplayedRowsForm(this.preferredTreadList);
        this.tableRef.resetFilters();
        this.tableRef.patchExtraFilters(this.filterFormBackend.value, true);
      }
    }
  }

  canDeactivate(): Promise<boolean> {
    if (this.checkIfModified()) {
      return new Promise<boolean>((resolve) => {
        this.showDiscardModal();
        const modalSubscription = this.discardModal.result.subscribe((result: ModalResult) => {
          if (result && result.id === 'discardConfirmation') {
            resolve(!!result.result);
            modalSubscription.unsubscribe();
          }
        });
      });
    }
    return Promise.resolve(true);
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any): void {
    if (this.checkIfModified()) {
      $event.returnValue = this.translationService.translate('discardChangesText');
    }
  }
}
